import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  fetchCurrentPeriodLeaderboard,
  fetchLeaderboardStats,
  fetchTopUsersForRewards,
  fetchUserLeaderboardPosition,
  selectCurrentPeriodData,
  selectCurrentPeriodError,
  selectCurrentPeriodLoading,
  selectCurrentPeriodPagination,
  selectStatsData,
  selectStatsLoading,
  selectTopUsersData,
  selectUserPositionData,
} from "../../../redux/slices/leaderboardSlice";
import { selectAppUser } from "../../redux/appAuthSlice";
import AppLeaderboardChrome from "../leaderboard/AppLeaderboardChrome";
import { fetchLeaderboardHistory } from "../leaderboard/fetchLeaderboardHistory";
import LeaderboardPodium from "../../../admin/leaderboard/LeaderboardPodium";
import LeaderboardResetClock from "../../../admin/leaderboard/LeaderboardResetClock";
import LeaderboardRewardLadder from "../../../admin/leaderboard/LeaderboardRewardLadder";
import LeaderboardStatsBand from "../../../admin/leaderboard/LeaderboardStatsBand";
import LeaderboardRankStream from "../../../admin/leaderboard/LeaderboardRankStream";
import LeaderboardHistoryPanel from "../../../admin/leaderboard/LeaderboardHistoryPanel";
import { matchesLeaderSearch } from "../../../admin/leaderboard/leaderboardHelpers";
import "../../../admin/leaderboard/admin-leaderboard.css";
import "../leaderboard/app-leaderboard.css";

const PAGE_SIZE = 20;

export default function DashboardLeaderboardPanel({ embedded = false }) {
  const dispatch = useDispatch();
  const user = useSelector(selectAppUser);
  const rows = useSelector(selectCurrentPeriodData);
  const loading = useSelector(selectCurrentPeriodLoading);
  const error = useSelector(selectCurrentPeriodError);
  const pagination = useSelector(selectCurrentPeriodPagination);
  const stats = useSelector(selectStatsData);
  const statsLoading = useSelector(selectStatsLoading);
  const topTen = useSelector(selectTopUsersData);
  const userPosition = useSelector(selectUserPositionData);

  const [view, setView] = useState("live");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [historyRefresh, setHistoryRefresh] = useState(0);

  const loadLive = useCallback(
    (nextPage = 1, { force = false } = {}) => {
      dispatch(fetchCurrentPeriodLeaderboard({ page: nextPage, limit: PAGE_SIZE, force }));
      dispatch(fetchLeaderboardStats({ force }));
      dispatch(fetchTopUsersForRewards({ limit: 10, force }));
      if (user?._id) {
        dispatch(fetchUserLeaderboardPosition({ userId: user._id, force }));
      }
    },
    [dispatch, user?._id]
  );

  useEffect(() => {
    if (view === "live") {
      loadLive(page);
    }
  }, [view, page, loadLive]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const filteredRows = useMemo(
    () => (rows || []).filter((row) => matchesLeaderSearch(row, search)),
    [rows, search]
  );

  const showPodium = page === 1 && !search;
  const topThree = showPodium ? filteredRows.slice(0, 3) : [];
  const streamRows = showPodium ? filteredRows.slice(3) : filteredRows;
  const yourRank = userPosition?.position ?? null;

  const handleRefresh = () => {
    if (view === "live") {
      loadLive(page, { force: true });
      return;
    }
    setHistoryRefresh((n) => n + 1);
  };

  return (
    <div className={`alb-page${embedded ? " alb-page-embedded" : ""}`}>
      <AppLeaderboardChrome
        embedded={embedded}
        view={view}
        onViewChange={setView}
        search={search}
        onSearchChange={setSearch}
        loading={view === "live" ? loading : false}
        onRefresh={handleRefresh}
        totalRanked={pagination.totalUsers}
        page={pagination.currentPage}
        totalPages={pagination.totalPages}
        yourRank={yourRank}
      />

      {view === "history" ? (
        <LeaderboardHistoryPanel
          fetchHistory={fetchLeaderboardHistory}
          refreshToken={historyRefresh}
        />
      ) : (
        <>
          <LeaderboardStatsBand
            stats={stats}
            totalRanked={pagination.totalUsers}
            loading={statsLoading}
          />

          {error ? (
            <div className="alb-error" role="alert">
              {error}
              <button type="button" onClick={() => loadLive(page)}>
                Retry
              </button>
            </div>
          ) : null}

          {showPodium ? (
            <div className="alb-hero-grid">
              <LeaderboardResetClock />
              <LeaderboardPodium topThree={topThree} />
              <LeaderboardRewardLadder topTen={topTen} />
            </div>
          ) : (
            <div className="alb-hero-compact">
              <LeaderboardResetClock />
              <LeaderboardRewardLadder topTen={topTen} />
            </div>
          )}

          <section className="alb-stream-panel" aria-label="Rankings">
            <header className="alb-stream-head">
              <div>
                <strong>{showPodium ? "The chase pack" : `Rankings · page ${page}`}</strong>
                <span>
                  {streamRows.length} member{streamRows.length === 1 ? "" : "s"}
                  {search ? ` matching “${search}”` : ""}
                </span>
              </div>
            </header>

            <LeaderboardRankStream
              rows={streamRows}
              loading={loading}
              emptyLabel={
                search
                  ? "No members match your search on this page."
                  : page === 1
                    ? "No ranked members beyond the podium yet."
                    : "No ranked members on this page."
              }
            />

            {pagination.totalPages > 1 && !search ? (
              <footer className="alb-pager">
                <button
                  type="button"
                  disabled={!pagination.hasPrev || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  aria-label="Previous page"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className="alb-pager-meta tabular-nums">
                  {pagination.currentPage} / {pagination.totalPages}
                </span>
                <button
                  type="button"
                  disabled={!pagination.hasNext || loading}
                  onClick={() => setPage((p) => p + 1)}
                  aria-label="Next page"
                >
                  <ChevronRight size={14} />
                </button>
              </footer>
            ) : null}
          </section>
        </>
      )}
    </div>
  );
}
